/**
 * HtmlDocument — the ONE HTML shell, rendered by BOTH render paths.
 *
 * SSOT for the document structure: the server (`server/render.tsx` via
 * `renderToReadableStream`) and the client (`app/entry-client.tsx` via
 * `hydrateRoot(document, …)`) render this SAME component. Because React owns
 * `<html>`/`<head>` on both sides, React 19 hoists `<title>`/`<meta>`/`<link>`
 * (from `SeoHead`, `Image`'s `<link rel="preload">`, …) into THIS `<head>`
 * symmetrically — so a server/client structure mismatch (the DOM-duplication +
 * hydration-error class the old hand-written string shell caused, because it
 * streamed the app into `#root` under a `<head>` React couldn't hoist into) is
 * unrepresentable.
 *
 * NO `<script>` nodes live here: the state globals (`window.__CONFIG__` …) and
 * the client module are injected by React as bootstrap (`bootstrapScriptContent`
 * / `bootstrapModules`), which React excludes from hydration — so the client
 * never has to byte-reproduce them. The dynamic head bits the client needs to
 * render an identical `<head>` (css + modulepreloads + locale) arrive via
 * `window.__SHELL__` (see `server/bootstrap.ts`), typed as [`ShellManifest`].
 *
 * A lint (`no-raw-doc-shell`) bans hand-written `<html>`/`<head>`/`<body>` /
 * `id="root"` HTML anywhere else, so a parallel shell that can't hoist can't
 * come back.
 */
import { VIEWPORT_META_CONTENT } from '@letsgethome/ui/z-layers';
import { useEffect, type ReactNode } from 'react';

/**
 * The attribute `<html>` carries once the client root has COMMITTED — the
 * document's "you may interact with me now" witness.
 *
 * # The class it closes
 *
 * `load` is not interactivity. A server-rendered document reaches it with every
 * control in the DOM and NONE of them wired: the markup is there, React has not
 * committed, and a JS-only affordance — a key handler, a control that calls a
 * mutation — is a no-op that reports nothing. The two states look identical to a
 * caller, which is why nothing noticed.
 *
 * That was harmless while every navigation the e2e suite drove was a CLIENT
 * route change: the app was already hydrated, so "the URL is right" implied "the
 * page is live". The native-forms work (#6118 → #6151 → #6181) removed the
 * implication — a `NativeForm` submitted before hydration is a REAL POST whose
 * 303 lands a fresh document, so waiting for the destination URL can now hand a
 * spec (or a reader) an inert page. Measured on the LET-80 staging smoke,
 * 2026-09-02/03: the inquiry submit went native, staging's own `web_ttfb_ms` was
 * reported for the conversation URL on all three attempts (a document
 * navigation), the composer's `Enter` landed before React attached its handler,
 * and the send produced ZERO `DirectService/SendMessage` calls — a red that read
 * as a product defect for fifteen hours.
 *
 * So the fact is PUBLISHED rather than guessed at. `web/e2e/helpers` waits for
 * this exact selector; the two spellings are held together by the loudest gate
 * available — a drift on either side fails every `navigateAndSettle` in the
 * suite at once, with a message that names the attribute and says what its
 * absence means. `HtmlDocument.react.test.tsx` pins this half in the PR lane.
 */
export const HYDRATED_ATTRIBUTE = 'data-hydrated';

/**
 * The per-render, build-derived head inputs the CLIENT must reproduce to render
 * a `<head>` identical to the server's (hydration match). Serialized into
 * `window.__SHELL__` by the server; read by `entry-client.tsx`. The static head
 * (charset/viewport/favicons/fonts) is constant and lives in the component.
 */
export interface ShellManifest {
  /** `<html lang>` — the request locale. */
  locale: string;
  /** Content-hashed stylesheet URLs (`/assets/*.css`). */
  cssFiles: string[];
  /** Content-hashed JS chunks to `<link rel="modulepreload">`. */
  preloadChunks: string[];
}

interface HtmlDocumentProps extends ShellManifest {
  /** The app subtree — `ShellProviders` wrapping the router (Static on the
   *  server, Browser on the client: the React Router hydration contract). */
  children: ReactNode;
}

/**
 * The full HTML document. `renderToReadableStream` emits `<!DOCTYPE html>` when
 * the root element is `<html>`. Title/meta/link are intentionally absent — they
 * are hoisted in from the app tree by React 19.
 */
export function HtmlDocument({ locale, cssFiles, preloadChunks, children }: HtmlDocumentProps) {
  // THE INTERACTIVE WITNESS (see [`HYDRATED_ATTRIBUTE`]). Effects flush
  // child-first, so this — the OUTERMOST component of the client tree — firing
  // means the whole app has committed and its handlers are attached. Server
  // rendering never runs an effect, so the attribute is absent from the HTML a
  // reader receives and appears only once the page can actually answer a click.
  // Idempotent, so StrictMode's double-invoke writes the same value twice.
  useEffect(() => {
    document.documentElement.setAttribute(HYDRATED_ATTRIBUTE, 'true');
  }, []);

  return (
    <html lang={locale} dir="ltr">
      <head>
        <meta charSet="utf-8" />
        {/*
          The viewport and the safe-area insets are ONE value, and this is the
          consuming half of it. `env(safe-area-inset-*)` — which every bottom
          band in `Z_LAYERS` reads — resolves to `0px` unless this content
          carries `viewport-fit=cover`, so a document that ships one without
          the other is stating two claims that only make sense together. That
          pairing used to live as a comment in `packages/ui` about this file.
          See `VIEWPORT_META_CONTENT` and `SAFE_AREA_INSETS_ARE_INERT`.
        */}
        <meta name="viewport" content={VIEWPORT_META_CONTENT} />
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="icon" href="/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/apple-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="theme-color" content="#18181b" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
        {cssFiles.map((href) => (
          <link key={href} rel="stylesheet" href={href} />
        ))}
        {preloadChunks.map((href) => (
          // `<link rel="modulepreload">` is a hint, not script/style — it carries
          // no CSP nonce, so the client can reproduce it from `__SHELL__` without
          // needing the server nonce.
          <link key={href} rel="modulepreload" href={href} />
        ))}
      </head>
      <body className="font-sans antialiased">
        <div id="root">{children}</div>
      </body>
    </html>
  );
}
